import {
  collectionGroup,
  doc,
  getDoc,
  getDocs
} from 'firebase/firestore'
import { getFactionStoredWrestlers } from './factionWrestlers'

const toDate = value => {
  if (!value) return null
  if (value instanceof Date) return value
  if (typeof value?.toDate === 'function') return value.toDate()
  if (typeof value?.seconds === 'number') return new Date(value.seconds * 1000)

  const parsed = new Date(value)
  return Number.isNaN(parsed.getTime()) ? null : parsed
}

const toFiniteNumber = value => {
  const number = Number(value)
  return Number.isFinite(number) ? number : 0
}

const getTimelinePriceValue = priceEntry =>
  toFiniteNumber(priceEntry?.value ?? priceEntry?.price ?? priceEntry)

const normalizeBonusValue = value =>
  String(value || '')
    .trim()
    .toLowerCase()
    .replace(/_ext$/, '')

const MATCH_BONUS_VALUES = new Set(['match_sett', 'match_ple'])
const WIN_BONUS_VALUES = new Set(['vince_sett', 'vince_ple'])
const APPEARANCE_BONUS_VALUES = new Set([
  'apparizione',
  'apparizione_ple',
  'main_event',
  'main_event_ple',
  'main_event_wm',
  'match_sett',
  'match_ple'
])
const PLE_SHOW_TYPES = new Set(['ple', 'ple-free'])

const isPleShowType = type => PLE_SHOW_TYPES.has(String(type || '').toLowerCase())

const hasAnyBonus = (participation, bonusValues) => {
  const bonuses = Array.isArray(participation?.bonuses) ? participation.bonuses : []
  return bonuses.some(bonus => bonusValues.has(normalizeBonusValue(bonus)))
}

const applyBonusRecord = (record, participation) => {
  const bonuses = Array.isArray(participation?.bonuses) ? participation.bonuses : []

  bonuses.forEach(bonus => {
    const normalizedBonus = normalizeBonusValue(bonus)
    if (MATCH_BONUS_VALUES.has(normalizedBonus)) record.losses += 1
    if (WIN_BONUS_VALUES.has(normalizedBonus)) {
      record.wins += 1
      record.losses -= 1
    }
  })
}

const extractShowIdFromRefPath = refPath => {
  const parts = String(refPath || '').split('/')
  const showIndex = parts.indexOf('shows')
  return showIndex >= 0 ? parts[showIndex + 1] || '' : ''
}

const isFutureShowDate = date =>
  date instanceof Date &&
  !Number.isNaN(date.getTime()) &&
  date.getTime() > Date.now()

const extractMatchParticipantIds = match => {
  const ids = new Set()
  const addId = value => {
    if (typeof value !== 'string' || !value || value.startsWith('guest_')) return
    ids.add(value)
  }
  const visitList = list => {
    if (!Array.isArray(list)) return
    list.forEach(entry => {
      if (typeof entry === 'string') {
        addId(entry)
        return
      }
      if (!entry || typeof entry !== 'object') return
      addId(entry.wrestlerId || entry.wrestlerID || entry.participantId)
      visitList(entry.participants)
      visitList(entry.participantIds)
      visitList(entry.wrestlers)
      visitList(entry.wrestlerIds)
    })
  }

  visitList(match?.participants)
  visitList(match?.participantIds)
  visitList(match?.wrestlers)
  visitList(match?.wrestlerIds)
  if (Array.isArray(match?.teams)) {
    match.teams.forEach(team => {
      visitList(team?.participants)
      visitList(team?.participantIds)
    })
  }

  return [...ids]
}

const loadShowInfoMap = async (db, showIds = []) => {
  const uniqueShowIds = [...new Set(showIds.filter(Boolean))]
  const showEntries = await Promise.all(
    uniqueShowIds.map(async showId => {
      const showSnap = await getDoc(doc(db, 'shows', showId))
      const showData = showSnap.exists() ? showSnap.data() : {}
      const type = showData.type || 'weekly'

      return [
        showId,
        {
          id: showId,
          name: showData.nome || showData.name || 'Show',
          date: toDate(showData.data || showData.date),
          type,
          isPle: isPleShowType(type)
        }
      ]
    })
  )

  return Object.fromEntries(showEntries)
}

export const getWrestlerCurrentPrice = wrestler => {
  const prices = Array.isArray(wrestler?.prices) ? wrestler.prices : []
  const latestPrice = prices[prices.length - 1]
  const timelinePrice = getTimelinePriceValue(latestPrice)

  if (timelinePrice > 0) return timelinePrice
  return toFiniteNumber(wrestler?.price ?? wrestler?.cost)
}

export const getWrestlerPriceTrend = wrestler => {
  const prices = Array.isArray(wrestler?.prices) ? wrestler.prices : []
  if (prices.length < 2) return 0

  const current = getTimelinePriceValue(prices[prices.length - 1])
  const previous = getTimelinePriceValue(prices[prices.length - 2])
  return current - previous
}

export const getWrestlerScoreTrend = wrestler => {
  const recentScores = Array.isArray(wrestler?.recentScores)
    ? wrestler.recentScores
    : Array.isArray(wrestler?.scores)
      ? wrestler.scores
      : []

  if (recentScores.length >= 2) {
    return (
      toFiniteNumber(recentScores[recentScores.length - 1]) -
      toFiniteNumber(recentScores[recentScores.length - 2])
    )
  }

  return toFiniteNumber(wrestler?.score)
}

const getTrendScore = wrestler =>
  getWrestlerScoreTrend(wrestler) * 2 +
  getWrestlerPriceTrend(wrestler) * 3 +
  toFiniteNumber(wrestler?.score) * 0.05

export const buildPremiumRemovalSuggestions = ({
  members = [],
  trendScoresById = {},
  limit: maxItems = 4
} = {}) =>
  members
    .filter(member => member?.id)
    .map(member => {
      const trendData = trendScoresById[member.id] || {}

      return {
        ...member,
        recentPoints: Number(trendData.recentPoints ?? 0) || 0,
        scoreTrend: Number(trendData.scoreTrend ?? 0) || 0
      }
    })
    .sort(
      (a, b) =>
        Number(b.active === false) - Number(a.active === false) ||
        Number(Boolean(b.isInjured)) - Number(Boolean(a.isInjured)) ||
        a.recentPoints - b.recentPoints ||
        a.scoreTrend - b.scoreTrend ||
        (a.name || '').localeCompare(b.name || '', 'it')
    )
    .slice(0, maxItems)

export const buildPremiumTradeSuggestions = ({
  targetFactions = [],
  freeAgents = [],
  wrestlersById = {},
  favoriteWrestlerIds = [],
  limit: maxItems = 6
} = {}) => {
  const favoriteSet = new Set(favoriteWrestlerIds)
  const factionCandidates = targetFactions.flatMap(faction =>
    getFactionStoredWrestlers(faction).map(entry => ({
      ...entry,
      ...(wrestlersById[entry.id] || {}),
      id: entry.id,
      wrestlerId: entry.id,
      sourceType: 'faction',
      sourceFactionId: faction.id,
      sourceName: faction.name || 'Altra fazione'
    }))
  )
  const freeAgentCandidates = freeAgents.map(wrestler => ({
    ...wrestler,
    sourceType: 'free_agent',
    sourceFactionId: '',
  }))

  const enriched = [...factionCandidates, ...freeAgentCandidates]
    .filter(wrestler => wrestler?.id && wrestler.active !== false)
    .map(wrestler => ({
      ...wrestler,
      isFavorite: favoriteSet.has(wrestler.id),
      currentPrice: getWrestlerCurrentPrice(wrestler),
      priceTrend: getWrestlerPriceTrend(wrestler),
      scoreTrend: getWrestlerScoreTrend(wrestler),
      trendScore: getTrendScore(wrestler)
    }))

  const rising = enriched.filter(
    wrestler => wrestler.priceTrend > 0 || wrestler.scoreTrend > 0
  )
  const source = rising.length > 0 ? rising : enriched

  return source
    .sort((a, b) => {
      if (a.isFavorite !== b.isFavorite) return a.isFavorite ? -1 : 1
      return (
        b.trendScore - a.trendScore ||
        toFiniteNumber(b.score) - toFiniteNumber(a.score) ||
        (a.name || '').localeCompare(b.name || '', 'it')
      )
    })
    .slice(0, maxItems)
}

export const loadPremiumWrestlerTrendScores = async ({
  db,
  wrestlerIds = []
} = {}) => {
  const uniqueIds = [...new Set(wrestlerIds.filter(Boolean))]
  if (!db || uniqueIds.length === 0) return {}

  const participationSlotsById = uniqueIds.reduce((acc, wrestlerId) => {
    acc[wrestlerId] = []
    return acc
  }, {})

  const participationsSnap = await getDocs(collectionGroup(db, 'participations'))
  const rawParticipations = participationsSnap.docs
    .map(docSnap => ({
      id: docSnap.id,
      ...docSnap.data(),
      showId: extractShowIdFromRefPath(docSnap.ref.path)
    }))
    .filter(participation => participationSlotsById[participation.wrestlerId])

  const showInfoMap = await loadShowInfoMap(
    db,
    rawParticipations.map(participation => participation.showId)
  )

  rawParticipations.forEach(participation => {
    participationSlotsById[participation.wrestlerId].push({
      points: toFiniteNumber(participation.totalPoints ?? participation.points),
      date: showInfoMap[participation.showId]?.date || null
    })
  })

  return Object.fromEntries(
    uniqueIds.map(wrestlerId => {
      const slots = [...(participationSlotsById[wrestlerId] || [])].sort(
        (a, b) =>
          (b.date?.getTime?.() || 0) - (a.date?.getTime?.() || 0)
      )
      const recentPoints = slots
        .slice(0, 3)
        .reduce((sum, entry) => sum + toFiniteNumber(entry.points), 0)
      const previousPoints = slots
        .slice(3, 6)
        .reduce((sum, entry) => sum + toFiniteNumber(entry.points), 0)

      return [
        wrestlerId,
        {
          recentScores: [previousPoints, recentPoints],
          recentPoints,
          previousPoints,
          scoreTrend: recentPoints - previousPoints
        }
      ]
    })
  )
}

export const loadPremiumWrestlerInsights = async ({
  db,
  wrestlerIds = [],
  wrestlersById = {}
} = {}) => {
  const uniqueIds = [...new Set(wrestlerIds.filter(Boolean))]
  if (!db || uniqueIds.length === 0) return {}

  const insights = uniqueIds.reduce((acc, wrestlerId) => {
    const wrestler = wrestlersById[wrestlerId] || {}
    acc[wrestlerId] = {
      wrestlerId,
      currentPrice: getWrestlerCurrentPrice(wrestler),
      teamComment: wrestler.premiumComment || '',
      lastThreeShows: [],
      lastThreeShowPoints: 0,
      announcedShows: [],
      upcomingPleMatches: [],
      record: { wins: 0, losses: 0 },
      lastAppearance: null,
      lastMatch: null
    }
    return acc
  }, {})

  const participationsSnap = await getDocs(collectionGroup(db, 'participations'))
  const rawParticipations = participationsSnap.docs
    .map(docSnap => ({
      id: docSnap.id,
      ...docSnap.data(),
      showId: extractShowIdFromRefPath(docSnap.ref.path)
    }))
    .filter(participation => insights[participation.wrestlerId])

  const showInfoMap = await loadShowInfoMap(
    db,
    rawParticipations.map(participation => participation.showId)
  )

  const participations = rawParticipations
    .map(participation => ({
      ...participation,
      show: showInfoMap[participation.showId] || {
        id: participation.showId,
        name: 'Show',
        date: null
      }
    }))
    .sort(
      (a, b) =>
        (b.show?.date?.getTime?.() || 0) - (a.show?.date?.getTime?.() || 0)
    )

  const announcedShowIdsByWrestler = new Map(
    uniqueIds.map(wrestlerId => [wrestlerId, new Set()])
  )
  const announcedPleMatchIdsByWrestler = new Map(
    uniqueIds.map(wrestlerId => [wrestlerId, new Set()])
  )
  const addAnnouncedShow = (wrestlerId, show, source = 'participation') => {
    const insight = insights[wrestlerId]
    if (!insight || !show?.id || !isFutureShowDate(show.date)) return

    const knownIds = announcedShowIdsByWrestler.get(wrestlerId)
    if (knownIds?.has(show.id)) return
    knownIds?.add(show.id)
    const isPleAnnouncement = Boolean(show.isPle) || isPleShowType(show.type)

    insight.announcedShows.push({
      showId: show.id,
      showName: show.name,
      date: show.date,
      showType: show.type || '',
      points: 0,
      isAnnouncement: true,
      isPleAnnouncement,
      source
    })

    if (isPleAnnouncement) {
      const knownPleIds = announcedPleMatchIdsByWrestler.get(wrestlerId)
      if (knownPleIds?.has(show.id)) return
      knownPleIds?.add(show.id)

      insight.upcomingPleMatches.push({
        showId: show.id,
        showName: show.name,
        date: show.date,
        showType: show.type || '',
        points: 0,
        isAnnouncement: true,
        isPleAnnouncement: true,
        source
      })
    }
  }

  participations.forEach(participation => {
    const wrestlerId = participation?.wrestlerId
    const insight = insights[wrestlerId]
    if (!insight) return

    const points = toFiniteNumber(participation.totalPoints ?? participation.points)
    applyBonusRecord(insight.record, participation)

    if (points > 0 && insight.lastThreeShows.length < 3) {
      insight.lastThreeShows.push({
        showId: participation.show.id,
        showName: participation.show.name,
        date: participation.show.date,
        points
      })
      insight.lastThreeShowPoints += points
    }

    if (points === 0) {
      addAnnouncedShow(wrestlerId, participation.show)
    }

    if (!insight.lastAppearance && hasAnyBonus(participation, APPEARANCE_BONUS_VALUES)) {
      insight.lastAppearance = {
        showId: participation.show.id,
        showName: participation.show.name,
        date: participation.show.date
      }
    }

    if (!insight.lastMatch && hasAnyBonus(participation, MATCH_BONUS_VALUES)) {
      insight.lastMatch = {
        showId: participation.show.id,
        showName: participation.show.name,
        date: participation.show.date
      }
    }
  })

  const wrestlerIdsNeedingMatchAnnouncements = new Set(
    uniqueIds.filter(
      wrestlerId => (insights[wrestlerId]?.upcomingPleMatches?.length || 0) === 0
    )
  )

  if (wrestlerIdsNeedingMatchAnnouncements.size > 0) {
    try {
      const matchesSnap = await getDocs(collectionGroup(db, 'matches'))
      const rawMatches = matchesSnap.docs
        .map(docSnap => ({
          id: docSnap.id,
          ...docSnap.data(),
          showId: extractShowIdFromRefPath(docSnap.ref.path)
        }))
        .filter(match =>
          match.showId &&
          extractMatchParticipantIds(match).some(wrestlerId =>
            wrestlerIdsNeedingMatchAnnouncements.has(wrestlerId)
          )
        )

      const matchShowInfoMap = await loadShowInfoMap(
        db,
        rawMatches.map(match => match.showId)
      )

      rawMatches.forEach(match => {
        const show = matchShowInfoMap[match.showId]
        if (!show || !isFutureShowDate(show.date)) return

        extractMatchParticipantIds(match).forEach(wrestlerId => {
          if (wrestlerIdsNeedingMatchAnnouncements.has(wrestlerId)) {
            addAnnouncedShow(wrestlerId, show, 'match')
          }
        })
      })
    } catch (error) {
      console.warn('Impossibile leggere le card match per gli annunci premium:', error)
    }
  }

  Object.values(insights).forEach(insight => {
    insight.announcedShows = insight.announcedShows
      .sort(
        (a, b) =>
          (a.date?.getTime?.() || 0) - (b.date?.getTime?.() || 0)
      )
      .slice(0, 3)
    insight.upcomingPleMatches = insight.upcomingPleMatches
      .sort(
        (a, b) =>
          (a.date?.getTime?.() || 0) - (b.date?.getTime?.() || 0)
      )
      .slice(0, 3)
  })

  return insights
}
